import type {
  CanonicalTreeEdgeKind,
} from './CanonicalTreeRelationshipEdge';

export type CanonicalTreeRelationshipPathClass =
  | 'STRUCTURAL_AUTHORED'
  | 'DERIVED_ONLY'
  | 'EXPLICIT_NON_ANCESTRAL_EDGE'
  | 'CHILD_MEDIATED'
  | 'AFFINITY_PARTNERSHIP_PATH'
  | 'HISTORICAL_EDGE'
  | 'INTENTIONALLY_UNRESOLVED';

export type CanonicalTreeAncestrySemantics =
  | 'REQUIRED'
  | 'FORBIDDEN'
  | 'DERIVED';

export interface CanonicalTreeRelationshipPathVariant {
  readonly id: string;
  readonly connectorRoles: readonly string[];
  readonly edgeKind: CanonicalTreeEdgeKind | null;
  readonly notes: string;
}

export interface CanonicalTreeRelationshipPathContract {
  readonly optionKey: string;
  readonly pathClass: CanonicalTreeRelationshipPathClass;
  readonly canonicalEdgeKind: CanonicalTreeEdgeKind | null;
  readonly ancestry: CanonicalTreeAncestrySemantics;
  readonly minSelectedConnectors: number;
  readonly maxSelectedConnectors: number;
  readonly connectorRoles: readonly string[];
  readonly allowFloating: boolean;
  readonly requiresExistingIdentityDecision: boolean;
  readonly multiplicity:
    | 'ONE_TO_ONE'
    | 'ONE_TO_MANY'
    | 'ONE_TO_MANY_SHARED_CHILDREN'
    | 'MULTIPATH'
    | 'NOT_APPLICABLE';
  readonly variants: readonly CanonicalTreeRelationshipPathVariant[];
  readonly notes: string;
}

const contract = (
  input: CanonicalTreeRelationshipPathContract,
): CanonicalTreeRelationshipPathContract =>
  Object.freeze({
    ...input,
    connectorRoles: Object.freeze([...input.connectorRoles]),
    variants: Object.freeze(
      input.variants.map(variant =>
        Object.freeze({
          ...variant,
          connectorRoles: Object.freeze([...variant.connectorRoles]),
        }),
      ),
    ),
  });

const structural = (
  optionKey: string,
  connectorRoles: readonly string[],
  notes: string,
  options: Partial<CanonicalTreeRelationshipPathContract> = {},
): CanonicalTreeRelationshipPathContract =>
  contract({
    optionKey,
    pathClass: 'STRUCTURAL_AUTHORED',
    canonicalEdgeKind: 'parent-child',
    ancestry: 'REQUIRED',
    minSelectedConnectors: connectorRoles.length,
    maxSelectedConnectors: connectorRoles.length,
    connectorRoles,
    allowFloating: false,
    requiresExistingIdentityDecision: false,
    multiplicity: 'ONE_TO_MANY',
    variants: [],
    notes,
    ...options,
  });

const explicit = (
  optionKey: string,
  edgeKind: CanonicalTreeEdgeKind,
  connectorRoles: readonly string[],
  notes: string,
  options: Partial<CanonicalTreeRelationshipPathContract> = {},
): CanonicalTreeRelationshipPathContract =>
  contract({
    optionKey,
    pathClass: 'EXPLICIT_NON_ANCESTRAL_EDGE',
    canonicalEdgeKind: edgeKind,
    ancestry: 'FORBIDDEN',
    minSelectedConnectors: connectorRoles.length,
    maxSelectedConnectors: connectorRoles.length,
    connectorRoles,
    allowFloating: false,
    requiresExistingIdentityDecision: false,
    multiplicity: 'ONE_TO_MANY',
    variants: [],
    notes,
    ...options,
  });

const partnership = (
  optionKey: string,
  connectorRoles: readonly string[],
  notes: string,
  options: Partial<CanonicalTreeRelationshipPathContract> = {},
): CanonicalTreeRelationshipPathContract =>
  contract({
    optionKey,
    pathClass: 'AFFINITY_PARTNERSHIP_PATH',
    canonicalEdgeKind: 'spouse-partner',
    ancestry: 'FORBIDDEN',
    minSelectedConnectors: connectorRoles.length,
    maxSelectedConnectors: connectorRoles.length,
    connectorRoles,
    allowFloating: false,
    requiresExistingIdentityDecision: false,
    multiplicity: 'MULTIPATH',
    variants: [],
    notes,
    ...options,
  });

const derived = (
  optionKey: string,
  notes: string,
): CanonicalTreeRelationshipPathContract =>
  contract({
    optionKey,
    pathClass: 'DERIVED_ONLY',
    canonicalEdgeKind: null,
    ancestry: 'DERIVED',
    minSelectedConnectors: 0,
    maxSelectedConnectors: 0,
    connectorRoles: [],
    allowFloating: false,
    requiresExistingIdentityDecision: false,
    multiplicity: 'NOT_APPLICABLE',
    variants: [],
    notes,
  });

/**
 * CC-TREE-LIVE-003G3WJ2
 *
 * A selectable relationship is complete only when it has truthful endpoints
 * and one governed canonical fact/edge path. Display text is never structural
 * authority. Viewer-relative kinship is derived after facts are authored.
 */
export const CANONICAL_TREE_RELATIONSHIP_PATH_CONTRACTS:
readonly CanonicalTreeRelationshipPathContract[] = Object.freeze([
  structural('mother', ['viewer-child'], 'Mother is authored as a parent-child edge into the viewer.'),
  structural('father', ['viewer-child'], 'Father is authored as a parent-child edge into the viewer.'),
  structural('child', ['parent'], 'Child requires at least one selected parent.', { minSelectedConnectors: 1, maxSelectedConnectors: 2 }),
  explicit('sibling', 'sibling', ['sibling-endpoint'], 'Sibling remains a lateral edge; shared parent facts may additionally prove siblinghood.'),
  partnership('spouse', ['partner'], 'Current spouse/partner is a symmetric partnership edge.'),
  structural('grandparent', ['parent'], 'Grandparent is parent of the selected parent.'),
  explicit('aunt-uncle', 'sibling', ['viewer-parent'], 'Aunt/uncle is sibling of the selected viewer parent.'),
  structural('cousin', ['aunt-uncle-parent'], 'Cousin is child of a selected aunt/uncle.', { minSelectedConnectors: 1, maxSelectedConnectors: 2 }),
  structural('cousin-child', ['cousin-parent'], 'Cousin child is child of a selected cousin.', { minSelectedConnectors: 1, maxSelectedConnectors: 2 }),
  structural('great-grandparent', ['grandparent'], 'Great-grandparent is parent of a selected grandparent.'),
  structural('v_great_great_grandparent', ['great-grandparent'], 'Great-great-grandparent is parent of a selected great-grandparent.'),
  structural('v_earlier_ancestor', ['immediate-descendant-ancestor'], 'Every deeper ancestor must attach to the immediately younger known ancestor.'),
  structural('v_grandchild', ['viewer-child'], 'Grandchild is child of a selected child of the viewer.', { minSelectedConnectors: 1, maxSelectedConnectors: 2 }),
  structural('v_great_grandchild', ['viewer-grandchild'], 'Great-grandchild is child of a selected grandchild.', { minSelectedConnectors: 1, maxSelectedConnectors: 2 }),
  explicit('v_great_aunt_uncle', 'sibling', ['viewer-grandparent'], 'Great-aunt/uncle is sibling of a selected grandparent.'),
  explicit('v_great_great_aunt_uncle', 'sibling', ['viewer-great-grandparent'], 'Great-great-aunt/uncle is sibling of a selected great-grandparent.'),
  structural('v_niece_nephew', ['viewer-sibling'], 'Niece/nephew is child of a selected sibling.', { minSelectedConnectors: 1, maxSelectedConnectors: 2 }),
  structural('v_great_niece_nephew', ['viewer-niece-nephew'], 'Great-niece/nephew is child of a selected niece/nephew.', { minSelectedConnectors: 1, maxSelectedConnectors: 2 }),
  derived('v_2nd_cousin', 'Second cousin is derived from common-ancestor parentage; direct label authoring is forbidden.'),
  derived('v_3rd_cousin', 'Third cousin is derived from common-ancestor parentage; direct label authoring is forbidden.'),
  derived('v_cousin_once_removed', 'Cousin removal is derived from generation depth and common ancestry.'),

  explicit('step-parent', 'step-family', ['viewer', 'viewer-parent'], 'Step-parent is non-ancestral and must be explained through the child plus the related parent.', { minSelectedConnectors: 2, maxSelectedConnectors: 2 }),
  explicit('v_step_mom', 'step-family', ['viewer', 'viewer-parent'], 'Step-mom is non-ancestral step-family evidence.', { minSelectedConnectors: 2, maxSelectedConnectors: 2 }),
  explicit('v_step_dad', 'step-family', ['viewer', 'viewer-parent'], 'Step-dad is non-ancestral step-family evidence.', { minSelectedConnectors: 2, maxSelectedConnectors: 2 }),
  structural('adopted-parent', ['viewer-child'], 'Adopted parentage is explicit structural parentage.'),
  explicit('v_step_sibling', 'step-family', ['sibling-endpoint', 'connecting-parent'], 'Step-sibling is explicit non-ancestral family evidence.', { minSelectedConnectors: 2, maxSelectedConnectors: 2 }),
  explicit('v_step_child', 'step-family', ['step-child', 'child-parent-partner'], 'Step-child is explicit non-ancestral family evidence.', { minSelectedConnectors: 2, maxSelectedConnectors: 2 }),
  structural('v_half_sibling', ['shared-parent'], 'Half-sibling must share at least one authored parent fact.', { minSelectedConnectors: 1, maxSelectedConnectors: 1 }),

  structural('v_mother_in_law', ['viewer-spouse'], 'Mother-in-law is authored as parent of the viewer spouse.'),
  structural('v_father_in_law', ['viewer-spouse'], 'Father-in-law is authored as parent of the viewer spouse.'),
  partnership('v_brother_in_law', ['affinity-endpoint'], 'Brother-in-law requires explicit disambiguation: sibling spouse OR spouse sibling.', {
    variants: [
      { id: 'SIBLING_SPOUSE', connectorRoles: ['viewer-sibling'], edgeKind: 'spouse-partner', notes: 'Person is spouse/partner of viewer sibling.' },
      { id: 'SPOUSE_SIBLING', connectorRoles: ['viewer-spouse'], edgeKind: 'sibling', notes: 'Person is sibling of viewer spouse.' },
    ],
  }),
  partnership('v_sister_in_law', ['affinity-endpoint'], 'Sister-in-law requires explicit disambiguation: sibling spouse OR spouse sibling.', {
    variants: [
      { id: 'SIBLING_SPOUSE', connectorRoles: ['viewer-sibling'], edgeKind: 'spouse-partner', notes: 'Person is spouse/partner of viewer sibling.' },
      { id: 'SPOUSE_SIBLING', connectorRoles: ['viewer-spouse'], edgeKind: 'sibling', notes: 'Person is sibling of viewer spouse.' },
    ],
  }),
  partnership('v_son_in_law', ['viewer-child'], 'Son-in-law is spouse/partner of a selected child.'),
  partnership('v_daughter_in_law', ['viewer-child'], 'Daughter-in-law is spouse/partner of a selected child.'),

  contract({ optionKey: 'v_former_spouse', pathClass: 'HISTORICAL_EDGE', canonicalEdgeKind: 'spouse-partner', ancestry: 'FORBIDDEN', minSelectedConnectors: 1, maxSelectedConnectors: 1, connectorRoles: ['former-partner'], allowFloating: false, requiresExistingIdentityDecision: false, multiplicity: 'MULTIPATH', variants: [], notes: 'Former spouse is preserved historical partnership evidence.' }),
  contract({ optionKey: 'v_former_partner', pathClass: 'HISTORICAL_EDGE', canonicalEdgeKind: 'spouse-partner', ancestry: 'FORBIDDEN', minSelectedConnectors: 1, maxSelectedConnectors: 1, connectorRoles: ['former-partner'], allowFloating: false, requiresExistingIdentityDecision: false, multiplicity: 'MULTIPATH', variants: [], notes: 'Former partner is preserved historical partnership evidence.' }),

  explicit('v_godparent', 'god-family', ['godchild'], 'Godparent requires an explicit god-family endpoint.'),
  explicit('v_godmother', 'god-family', ['godchild'], 'Godmother requires an explicit god-family endpoint.'),
  explicit('v_godfather', 'god-family', ['godchild'], 'Godfather requires an explicit god-family endpoint.'),
  explicit('v_godchild', 'god-family', ['godparent'], 'Godchild requires an explicit god-family endpoint.'),
  explicit('v_chosen_parent', 'chosen-family', ['chosen-child'], 'Chosen parent is explicit chosen-family evidence.'),
  explicit('v_chosen_child', 'chosen-family', ['chosen-parent'], 'Chosen child is explicit chosen-family evidence.'),
  explicit('v_chosen_sibling', 'chosen-family', ['chosen-sibling'], 'Chosen sibling is explicit chosen-family evidence.'),
  explicit('v_chosen_aunt_uncle', 'chosen-family', ['chosen-relative'], 'Chosen aunt/uncle is explicit chosen-family evidence.'),
  explicit('v_family_friend', 'chosen-family', ['family-anchor'], 'Family friend is explicit non-transitive family affiliation.', { multiplicity: 'MULTIPATH' }),

  contract({
    optionKey: 'v_coparent',
    pathClass: 'CHILD_MEDIATED',
    canonicalEdgeKind: 'co-parent',
    ancestry: 'FORBIDDEN',
    minSelectedConnectors: 1,
    maxSelectedConnectors: Number.MAX_SAFE_INTEGER,
    connectorRoles: ['shared-child'],
    allowFloating: false,
    requiresExistingIdentityDecision: true,
    multiplicity: 'ONE_TO_MANY_SHARED_CHILDREN',
    variants: [],
    notes: 'One canonical co-parent identity may share any number of children; each child retains both adult parent endpoints.',
  }),
  explicit('v_guardian', 'guardian', ['ward'], 'Guardian requires an explicit ward endpoint.'),
  explicit('v_foster_family', 'foster-family', ['foster-relative'], 'Foster family requires an explicit family endpoint.'),
  explicit('v_affiliated_family', 'affiliated-family', ['affiliation-anchor'], 'Affiliated family requires an explicit connector endpoint.', { multiplicity: 'MULTIPATH' }),

  contract({
    optionKey: 'unknown',
    pathClass: 'INTENTIONALLY_UNRESOLVED',
    canonicalEdgeKind: null,
    ancestry: 'FORBIDDEN',
    minSelectedConnectors: 0,
    maxSelectedConnectors: 0,
    connectorRoles: [],
    allowFloating: true,
    requiresExistingIdentityDecision: false,
    multiplicity: 'NOT_APPLICABLE',
    variants: [],
    notes: 'Unknown/Possible Relation is the only intentionally floating relationship option.',
  }),
]);

const REQUIRED_OPTION_KEYS = Object.freeze([
  'mother','father','child','sibling','spouse','grandparent','aunt-uncle','cousin','cousin-child','great-grandparent',
  'v_great_great_grandparent','v_earlier_ancestor','v_grandchild','v_great_grandchild','v_great_aunt_uncle','v_great_great_aunt_uncle',
  'v_niece_nephew','v_great_niece_nephew','v_2nd_cousin','v_3rd_cousin','v_cousin_once_removed','step-parent','v_step_mom','v_step_dad',
  'adopted-parent','v_step_sibling','v_step_child','v_half_sibling','v_mother_in_law','v_father_in_law','v_brother_in_law','v_sister_in_law',
  'v_son_in_law','v_daughter_in_law','v_former_spouse','v_former_partner','v_godparent','v_godmother','v_godfather','v_godchild',
  'v_chosen_parent','v_chosen_child','v_chosen_sibling','v_chosen_aunt_uncle','v_family_friend','v_coparent','v_guardian','v_foster_family',
  'v_affiliated_family','unknown',
]);

export interface CanonicalTreeRelationshipPathVerification {
  readonly passed: boolean;
  readonly contractCount: number;
  readonly uniqueOptionCount: number;
  readonly missingOptionKeys: readonly string[];
  readonly duplicateOptionKeys: readonly string[];
  readonly invalidNonAncestralOptionKeys: readonly string[];
  readonly verifiedAt: string;
}

export function getCanonicalTreeRelationshipPathContracts():
readonly CanonicalTreeRelationshipPathContract[] {
  return CANONICAL_TREE_RELATIONSHIP_PATH_CONTRACTS;
}

export function getCanonicalTreeRelationshipPathContract(
  optionKey: string,
): CanonicalTreeRelationshipPathContract | null {
  return CANONICAL_TREE_RELATIONSHIP_PATH_CONTRACTS.find(
    record => record.optionKey === optionKey,
  ) ?? null;
}

export function verifyCanonicalTreeRelationshipPathContracts():
CanonicalTreeRelationshipPathVerification {
  const counts = new Map<string, number>();
  for (const record of CANONICAL_TREE_RELATIONSHIP_PATH_CONTRACTS) {
    counts.set(record.optionKey, (counts.get(record.optionKey) ?? 0) + 1);
  }

  const missingOptionKeys = REQUIRED_OPTION_KEYS.filter(
    key => !counts.has(key),
  );
  const duplicateOptionKeys = [...counts.entries()]
    .filter(([, count]) => count !== 1)
    .map(([key]) => key);

  const invalidNonAncestralOptionKeys =
    CANONICAL_TREE_RELATIONSHIP_PATH_CONTRACTS
      .filter(record =>
        [
          'EXPLICIT_NON_ANCESTRAL_EDGE',
          'CHILD_MEDIATED',
          'AFFINITY_PARTNERSHIP_PATH',
          'HISTORICAL_EDGE',
        ].includes(record.pathClass) &&
        record.ancestry !== 'FORBIDDEN',
      )
      .map(record => record.optionKey);

  const passed =
    CANONICAL_TREE_RELATIONSHIP_PATH_CONTRACTS.length === 50 &&
    counts.size === 50 &&
    missingOptionKeys.length === 0 &&
    duplicateOptionKeys.length === 0 &&
    invalidNonAncestralOptionKeys.length === 0;

  return Object.freeze({
    passed,
    contractCount: CANONICAL_TREE_RELATIONSHIP_PATH_CONTRACTS.length,
    uniqueOptionCount: counts.size,
    missingOptionKeys: Object.freeze(missingOptionKeys),
    duplicateOptionKeys: Object.freeze(duplicateOptionKeys),
    invalidNonAncestralOptionKeys: Object.freeze(invalidNonAncestralOptionKeys),
    verifiedAt: new Date().toISOString(),
  });
}
